import { type DownloadShippingLabelResponse } from './aliases';
import {
  type EstimatedShippingProviderParcelSizeQuery,
  type MarkAsShippedRequest,
  type ShippingProviderParcelSizeResponse,
} from './types';

import {
  ADD_USER_TO_CAMPAIGN_ENDPOINT,
  AWAITING_SHIPPING_COUNT,
  BULK_SHIPPING_LABELS_ENDPOINT,
  ESTIMATED_SHIPPING_PROVIDERS_ENDPOINT,
  MANUAL_SHIPPING_PROVIDERS_ENDPOINT,
  MARK_AS_SHIPPED_ENDPOINT,
  REQUESTED_DEADLINE_EXTENSIONS_COUNT,
  SHIPPING_EXTENSION_ENDPOINT,
  SHIPPING_EXTENSION_OPTIONS_ENDPOINT,
  SHIPPING_LABEL_DOWNLOAD_ENDPOINT,
  SHIPPING_SETTINGS_ENDPOINT,
  SHIPPING_PROVIDERS_ENDPOINT,
} from '@/constants/endpoints';
import http, { typedHttp } from '@/modules/http';
import { replacePathParams } from '@/modules/http/replacePathParams';
import { type RequestBody } from '@/modules/http/type-helpers';
import { type AxiosCompatibleResponse } from '@/modules/http/types';

export function updateShippingPreferences(
  countryCode: string,
  data: RequestBody<typeof SHIPPING_SETTINGS_ENDPOINT, 'post'>
) {
  return typedHttp.post(SHIPPING_SETTINGS_ENDPOINT, data, {
    withAuth: true,
    headers: {
      ['x-country-code']: countryCode,
    },
  });
}

export function markAsShipped(data: MarkAsShippedRequest) {
  return typedHttp.patch(
    replacePathParams(MARK_AS_SHIPPED_ENDPOINT, { parcelId: data.parcelId }),
    { carrier_name: data.carrierName, tracking_number: data.trackingNumber },
    {
      withAuth: true,
    }
  );
}

export function getManualShippingProviders({ country }: { country: string }) {
  return typedHttp.get(MANUAL_SHIPPING_PROVIDERS_ENDPOINT, {
    params: {
      country,
    },
    withAuth: true,
  });
}

export function downloadShippingLabel({
  parcelId,
  timeout,
}: {
  parcelId: string;
  timeout?: number;
}): Promise<AxiosCompatibleResponse<DownloadShippingLabelResponse>> {
  return typedHttp.get(
    replacePathParams(SHIPPING_LABEL_DOWNLOAD_ENDPOINT, { parcelId }),
    {
      withAuth: true,
      timeout,
    }
  );
}

export function getShippingPreferences(countryCode: string) {
  return typedHttp.get(SHIPPING_SETTINGS_ENDPOINT, {
    withAuth: true,
    headers: {
      ['x-country-code']: countryCode,
    },
  });
}

export function evaluateShippingSettings() {
  return typedHttp.post(SHIPPING_SETTINGS_ENDPOINT, {}, { withAuth: true });
}

export function getShippingProvidersInfo(
  shippingProvidersIds: string,
  countryCode: string,
  itemCost?: string,
  itemCurrency?: string
): Promise<AxiosCompatibleResponse<ShippingProviderParcelSizeResponse[]>> {
  return http.get(SHIPPING_PROVIDERS_ENDPOINT, {
    params: {
      ids: shippingProvidersIds,
      itemCost,
      itemCurrency,
    },
    withAuth: true,
    headers: {
      ['x-country-code']: countryCode,
    },
  });
}

export function getEstimatedShippingProvidersInfo(
  params: EstimatedShippingProviderParcelSizeQuery,
  countryCode: string
) {
  return typedHttp.get(ESTIMATED_SHIPPING_PROVIDERS_ENDPOINT, {
    params,
    withAuth: true,
    headers: {
      ['x-country-code']: countryCode,
    },
  });
}

export function getAwaitingShippingCount() {
  return typedHttp.get(AWAITING_SHIPPING_COUNT, {
    withAuth: true,
  });
}

export function getRequestedDeadlineExtensionsCount() {
  return typedHttp.get(REQUESTED_DEADLINE_EXTENSIONS_COUNT, {
    withAuth: true,
  });
}

export function respondToDeadlineExtension({
  purchaseId,
  reply,
}: {
  purchaseId: string;
  reply: RequestBody<typeof SHIPPING_EXTENSION_ENDPOINT>['reply'];
}) {
  return typedHttp.post(
    replacePathParams(SHIPPING_EXTENSION_ENDPOINT, { purchaseId }),
    { reply },
    { withAuth: true }
  );
}

export function getShippingExtensionOptions(purchaseId: number) {
  return typedHttp.get(
    replacePathParams(SHIPPING_EXTENSION_OPTIONS_ENDPOINT, {
      purchaseId: String(purchaseId),
    }),
    {
      withAuth: true,
    }
  );
}

export function requestShippingExtension(
  purchaseId: number,
  data: RequestBody<typeof SHIPPING_EXTENSION_ENDPOINT, 'put'>
) {
  return typedHttp.put(
    replacePathParams(SHIPPING_EXTENSION_ENDPOINT, {
      purchaseId: String(purchaseId),
    }),
    data,
    {
      withAuth: true,
    }
  );
}

export function downloadBulkShippingLabels(
  data: RequestBody<typeof BULK_SHIPPING_LABELS_ENDPOINT>
) {
  return typedHttp.post(BULK_SHIPPING_LABELS_ENDPOINT, data, {
    withAuth: true,
  });
}

export function fetchOrAddUserToCampaign() {
  return typedHttp.post(ADD_USER_TO_CAMPAIGN_ENDPOINT, null, {
    withAuth: true,
  });
}
